import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import locations from '../data/locations.json';
import { useLocationRoute } from '../data/locationRoutes';
import { mapUrl } from './LocationInfo';
import './ServicePage.css';

export default function ServicePage({ service }) {
    const { shop: selected, to } = useLocationRoute();
    const shops = selected ? [selected] : locations;
    const title = service.seoTitle || `${service.title} | GOLDWrench Automotive`;
    const url = `https://www.goldwrenchauto.com${service.path}`;
    const breadcrumbs = { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.goldwrenchauto.com/' },
        ...(selected ? [{ '@type': 'ListItem', position: 2, name: selected.city, item: `https://www.goldwrenchauto.com/${selected.id}` }] : []),
        { '@type': 'ListItem', position: selected ? 3 : 2, name: service.label, item: `https://www.goldwrenchauto.com${to(service.path)}` }
    ] };
    return <article className="service-page">
        <Helmet>
            <title>{title}</title>
            <meta name="description" content={service.description} />
            <meta property="og:type" content="website" />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={service.description} />
            <meta property="og:url" content={url} />
            <script type="application/ld+json">{JSON.stringify(breadcrumbs)}</script>
        </Helmet>
        <nav className="service-breadcrumbs" aria-label="Breadcrumb"><Link to="/">Home</Link><span aria-hidden="true">/</span>{selected && <><Link to={to('/')}>{selected.city}</Link><span aria-hidden="true">/</span></>}<span aria-current="page">{service.label}</span></nav>
        <div className="service-layout">
            <header className="service-intro">
                <p className="service-eyebrow">GOLDWrench Automotive{selected ? ` · ${selected.city}` : ''}</p>
                <h1>{service.title}</h1>
                <p className="service-lede">{service.intro}</p>
                <a className="service-jump" href="#service-locations">{selected ? `Contact ${selected.city}` : 'Contact a shop'} <span aria-hidden="true">↓</span></a>
            </header>
            <aside id="service-locations" className={`service-contact${selected ? ' service-contact--selected' : ''}`} aria-label="Contact a shop about this service">
                <h2>{selected ? `Contact ${selected.city}` : 'Talk to your shop'}</h2>
                <p className="service-availability">Call to confirm service availability and arrange your visit.</p>
                {shops.map(shop => <section key={shop.id} className="service-shop" aria-label={`${shop.city} service contact`}>
                    <h3><Link to={`/${shop.id}`}>{shop.city}, VA</Link></h3>
                    <p>{shop.street}<br />{shop.city}, VA {shop.zip}</p>
                    <a className="service-call" href={`tel:${shop.phone}`} aria-label={`Call ${shop.city} about ${service.label.toLowerCase()}`}>
                        <span>Call {shop.city}</span><span>{shop.displayPhone}</span>
                    </a>
                    {selected && <dl className="service-hours">{shop.hours.map(([day, hours]) => <div key={day}><dt>{day}</dt><dd>{hours}</dd></div>)}</dl>}
                    <div className="service-shop-links"><Link to={`/${shop.id}/contact`}>Hours &amp; shop details</Link><a href={mapUrl(shop)}>Directions</a></div>
                </section>)}
            </aside>
            <div className="service-body">
                <section aria-labelledby="service-details-heading">
                    <h2 id="service-details-heading">{service.detailsTitle}</h2>
                    <p>{service.detailsIntro}</p>
                    <ul className="service-checklist">{service.items.map(item => <li key={item}>{item}</li>)}</ul>
                    {service.note && <p className="service-note">{service.note}</p>}
                </section>
                {service.sections?.map(section => <section className="service-explanation" key={section.id} id={section.id} aria-labelledby={`${section.id}-heading`}>
                    <h2 id={`${section.id}-heading`}>{section.heading}</h2>
                    <p>{section.text}</p>
                    {section.link && <Link to={to(section.link)}>{section.linkText}</Link>}
                </section>)}
                <section className="service-next" aria-labelledby="service-next-heading">
                    <h2 id="service-next-heading">{service.nextTitle}</h2>
                    <p>{service.nextText}</p>
                    <Link to={to('/contact')}>{selected ? `${selected.city} hours and directions` : 'View both locations and opening hours'} <span aria-hidden="true">→</span></Link>
                </section>
                <nav className="service-related" aria-label="Other services">
                    <span>Other services</span>
                    {[
                        ['/autorepairs', 'Auto repair'], ['/tires', 'Tires'], ['/stateinspection', 'State inspection'], ['/towingservice', 'Towing']
                    ].filter(([path]) => path !== service.path).map(([path, label]) => <Link key={path} to={to(path)}>{label}</Link>)}
                </nav>
            </div>
        </div>
    </article>;
}
